import { isSameDay, isSameWeek, subDays } from 'date-fns';

import {
    CalendarTodayOutlined,
    CheckCircleOutline,
    DescriptionOutlined,
    FolderOutlined,
    PencilLineIcon,
    ReceiptLongOutlined,
    TaskAlt,
    VerifiedUserOutlined,
    type SvgIconComponent,
} from 'components/icons';
import { AutomatedNotification, NotificationCategory } from 'types/notifications';
import { familyTileClass, NEUTRAL_TILE_CLASS } from 'utils/families';

export type InboxCategoryId =
    | 'invoicing'
    | 'cases-and-clients'
    | 'correspondence'
    | 'deadlines'
    | 'tasks'
    | 'compliance'
    | 'drafts'
    | 'compliance-tasks';

export interface InboxCategory {
    id: InboxCategoryId;
    labelId: string;
    icon: SvgIconComponent;
    /**
     * The square behind the row's and the filter menu's glyph, fill and ink: its family's tile, the neutral tile for
     * a category of none, or its own role's colours.
     */
    tileClassName: string;
    /** The backend category its notifications carry; compliance tasks have none. */
    notificationCategory?: NotificationCategory;
}

/** Every Inbox category, in the filter menu's order; Correspondence only with its module on. */
export const INBOX_CATEGORIES: InboxCategory[] = [
    {
        id: 'invoicing',
        labelId: 'notifications.invoicing',
        icon: ReceiptLongOutlined,
        tileClassName: familyTileClass('finance'),
        notificationCategory: NotificationCategory.INVOICING,
    },
    {
        id: 'cases-and-clients',
        labelId: 'notifications.cases-and-clients',
        icon: FolderOutlined,
        tileClassName: familyTileClass('cases'),
        notificationCategory: NotificationCategory.CASE_AND_CLIENT,
    },
    {
        id: 'correspondence',
        labelId: 'notifications.correspondence',
        icon: DescriptionOutlined,
        tileClassName: familyTileClass('cases'),
        notificationCategory: NotificationCategory.CORRESPONDENCE,
    },
    {
        id: 'deadlines',
        labelId: 'notifications.deadlines',
        icon: CalendarTodayOutlined,
        tileClassName: NEUTRAL_TILE_CLASS,
        notificationCategory: NotificationCategory.DEADLINES,
    },
    {
        id: 'tasks',
        labelId: 'notifications.tasks',
        icon: TaskAlt,
        tileClassName: NEUTRAL_TILE_CLASS,
        notificationCategory: NotificationCategory.TASKS,
    },
    {
        id: 'compliance',
        labelId: 'notifications.compliance',
        icon: VerifiedUserOutlined,
        tileClassName: familyTileClass('compliance'),
        notificationCategory: NotificationCategory.COMPLIANCE,
    },
    // A draft's row draws its record's tile instead (`draftTile`); this one is the filter menu's.
    {
        id: 'drafts',
        labelId: 'notifications.drafts',
        icon: PencilLineIcon,
        tileClassName: 'bg-changed text-changed-ink',
        notificationCategory: NotificationCategory.DRAFTS,
    },
    // Not "Compliance": the notification category of that name is a category of its own.
    {
        id: 'compliance-tasks',
        labelId: 'menu.inbox-compliance-tasks',
        icon: CheckCircleOutline,
        tileClassName: NEUTRAL_TILE_CLASS,
    },
];

/** One dated row of the feed: a notification addressed to the user. */
export type FeedEntry = { category: InboxCategory; item: AutomatedNotification };

export type DayGroupId = 'today' | 'yesterday' | 'earlier-this-week' | 'older';

export interface DayGroup {
    id: DayGroupId;
    entries: FeedEntry[];
}

/**
 * The day heading a row falls under, by the local calendar: the week starts on Monday. A row stamped
 * after `now` (a clock ahead of this one) counts as today, so the newest-first feed stays in heading order.
 */
export function dayGroupOf(date: Date, now: Date): DayGroupId {
    if (date >= now || isSameDay(date, now)) {
        return 'today';
    }
    if (isSameDay(date, subDays(now, 1))) {
        return 'yesterday';
    }
    if (isSameWeek(date, now, { weekStartsOn: 1 })) {
        return 'earlier-this-week';
    }
    return 'older';
}

/** The feed newest first, cut into its day headings; a heading with no rows is left out. */
export function groupByDay(entries: FeedEntry[], now: Date): DayGroup[] {
    const sorted = [...entries].sort(
        (a, b) => new Date(b.item.createdAt).getTime() - new Date(a.item.createdAt).getTime()
    );
    const groups: DayGroup[] = [];
    for (const entry of sorted) {
        const id = dayGroupOf(new Date(entry.item.createdAt), now);
        const last = groups.at(-1);
        if (last?.id === id) {
            last.entries.push(entry);
        } else {
            groups.push({ id, entries: [entry] });
        }
    }
    return groups;
}
